import { useMutation, useQueryClient } from "react-query";
import { RestError } from "../../services/rest-service/RestService";
import { IUICustomizationPresetDescriptionDTO } from "../../services/ui-customization-data-service/Models";
import UICustomizationDataService from "../../services/ui-customization-data-service/UICustomizationDataService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { MutationContextType } from "./Common";
import { keys } from "./Keys";
import { keys as userKeys } from "../user-role-administration/Keys";

export type UseDeleteUICustomizationPresetResultType = {
    presetName: string | undefined;
};

export type UseDeleteUICustomizationPresetParameters = {
    groupID: number;
    organizationID: number;
} & BaseUseMutationParameters<UseDeleteUICustomizationPresetResultType, IUICustomizationPresetDescriptionDTO>;

export function useDeleteUICustomizationPreset({
    groupID,
    organizationID,
    onError,
    onSuccess
}: UseDeleteUICustomizationPresetParameters) {
    const queryClient = useQueryClient();

    return useMutation<UseDeleteUICustomizationPresetResultType, RestError, IUICustomizationPresetDescriptionDTO, MutationContextType>(
        async (preset) => {
            await UICustomizationDataService.deleteUICustomizationPreset(groupID, preset.id);

            return {
                presetName: preset.name
            };
        },
        {
            onMutate: async (preset: IUICustomizationPresetDescriptionDTO) => {

                await queryClient.cancelQueries(keys.all);

                const previousPresets = queryClient.getQueryData<Array<IUICustomizationPresetDescriptionDTO>>(
                    keys.presetList(
                        organizationID
                    )
                );
                queryClient.setQueryData<Array<IUICustomizationPresetDescriptionDTO>>(
                    keys.presetList(
                        organizationID
                    ),
                    (old: Array<IUICustomizationPresetDescriptionDTO> | undefined) => {
                        const copy = old?.filter(p => p.id !== preset.id) ?? [];
                        return copy;
                    }
                );

                return {
                    previousPresets: previousPresets
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_, error, preset, context) => {

                if (error && context?.previousPresets) {
                    queryClient.setQueryData<Array<IUICustomizationPresetDescriptionDTO>>(
                        keys.presetList(
                            organizationID
                        ),
                        context?.previousPresets
                    );
                }

                if (!error) {
                    queryClient.invalidateQueries(keys.settings());
                    queryClient.invalidateQueries(keys.effectiveSettings());
                    queryClient.invalidateQueries(keys.presetList(organizationID));
                    queryClient.invalidateQueries(keys.preset(preset.id));
                    queryClient.invalidateQueries(keys.presetUserList(preset.id));
                    queryClient.invalidateQueries(userKeys.userLists());
                }
            }
        }
    );
}
